/* ============================================================================
 * App registry — the executables the OS can mount
 * ----------------------------------------------------------------------------
 * Mirrors the DESIGN SOURCE registry (SupercomputeA/supercompute-immersion →
 * supercompute-shell.js `APPS`), which is the authoritative list of desks and
 * the ids the dock tiles are named after.
 *
 * The design source lists 13 entries (a Desk launcher + 12 apps). All twelve
 * desks are implemented here (the five previously unbuilt desks — home-office,
 * reserve-capital, calendar, community, vendor — are ported from the static
 * source).
 *
 * Ids match the design source exactly so `dock-<id>.png` resolves. The one
 * exception is school, whose tile is `dock-projects-school.png` — the design
 * shell rewrites it the same way.
 * ========================================================================= */

import type { ComponentType } from 'react';
import { DigitalEmbassy } from './DigitalEmbassy';
import { NewsDesk } from './NewsDesk';
import { TradeDesk } from './TradeDesk';
import { TheSchool } from './TheSchool';
import { StoreFront } from './StoreFront';
import { Channel } from './Channel';
import { LiveTv } from './LiveTv';
import { WalletDesk } from './WalletDesk';
import { Settings } from './Settings';
import { SkinBuilder } from './SkinBuilder';
import { ImmersionWizard } from './ImmersionWizard';
import { HomeOffice } from './HomeOffice';
import { ReserveCapital } from './ReserveCapital';
import { Calendar } from './Calendar';
import { Community } from './Community';
import { Vendor } from './Vendor';

export interface AppDef {
  /** Matches the design source app id; also used as the window's appId. */
  id: string;
  /** Short label for the dock tile, as the design source names it. */
  label: string;
  /** Displayed in the titlebar. */
  title: string;
  /** A single glyph, used on desktop icons where a tile is not wanted. */
  glyph: string;
  /** Dock tile, served from /logos (copied from the design source). */
  tile: string;
  defaultSize: { w: number; h: number };
  blurb: string;
  /** null ⇒ specified in the design source but not implemented in this repo. */
  component: ComponentType | null;
}

function tileFor(id: string): string {
  return `/logos/dock-${id === 'school' ? 'projects-school' : id}.png`;
}

export const APPS: readonly AppDef[] = [
  {
    id: 'embassy',
    label: 'Embassy',
    title: 'EMBASSY.EXE',
    glyph: '◈',
    tile: tileFor('embassy'),
    defaultSize: { w: 680, h: 620 },
    blurb: 'Sovereign pass — ENS identity, EAS stamps, credentials, interop.',
    component: DigitalEmbassy,
  },
  {
    id: 'newsdesk',
    label: 'News desk',
    title: 'NEWS_DESK.EXE',
    glyph: '▤',
    tile: tileFor('newsdesk'),
    defaultSize: { w: 700, h: 640 },
    blurb: 'Write it, split it, prove it — presence, moments, dispatch, lookup.',
    component: NewsDesk,
  },
  {
    id: 'tradedesk',
    label: 'Trade desk',
    title: 'TRADE_DESK.EXE',
    glyph: '◎',
    tile: tileFor('tradedesk'),
    defaultSize: { w: 520, h: 500 },
    blurb: 'Treasury, 0xSplits and Aragon.',
    component: TradeDesk,
  },
  {
    id: 'school',
    label: 'School',
    title: 'THE_SCHOOL',
    glyph: '⌘',
    tile: tileFor('school'),
    defaultSize: { w: 760, h: 640 },
    blurb: 'Cohorts that learn the rails — tracks, cohorts, Presence certification.',
    component: TheSchool,
  },
  {
    id: 'channel',
    label: 'Channel',
    title: 'CHANNEL.EXE',
    glyph: '◉',
    tile: tileFor('channel'),
    defaultSize: { w: 540, h: 500 },
    blurb: 'Live channels and Farcaster syndication.',
    component: Channel,
  },
  {
    id: 'live-tv',
    label: 'Live TV',
    title: 'LIVE_TV.EXE',
    glyph: '▭',
    // Compiled by scripts/pixelgen.py to public/pixel, not a dock-*.png — the
    // tile is drawn on the dock's own 32 px grid against the design palette.
    tile: '/pixel/dock-live-tv.png',
    defaultSize: { w: 620, h: 560 },
    blurb: 'The broadcast desk — a real player for operator-supplied sources.',
    component: LiveTv,
  },
  {
    id: 'storefront',
    label: 'Storefront',
    title: 'STOREFRONT.EXE',
    glyph: '▣',
    tile: tileFor('storefront'),
    defaultSize: { w: 560, h: 480 },
    blurb: 'Sovereign commerce — merch and digital goods.',
    component: StoreFront,
  },
  {
    id: 'wallet',
    label: 'Wallet',
    title: 'WALLET.EXE',
    glyph: '⛁',
    tile: tileFor('wallet'),
    defaultSize: { w: 520, h: 520 },
    blurb: 'Assets, consent collection and waivers.',
    component: WalletDesk,
  },

  /* ── Ported from the static design source ─────────────────────────────── */
  {
    id: 'home-office',
    label: 'Home office',
    title: 'HOME_OFFICE.EXE',
    glyph: '⌂',
    tile: tileFor('home-office'),
    defaultSize: { w: 540, h: 500 },
    blurb: 'Enzyme: runsheet, payables, supplies, switches.',
    component: HomeOffice,
  },
  {
    id: 'reserve-capital',
    label: 'Reserve',
    title: 'RESERVE_CAPITAL.EXE',
    glyph: '▦',
    tile: tileFor('reserve-capital'),
    defaultSize: { w: 540, h: 500 },
    blurb: 'Token value, splits, reserve.',
    component: ReserveCapital,
  },
  {
    id: 'calendar',
    label: 'Calendar',
    title: 'CALENDAR.EXE',
    glyph: '▦',
    tile: tileFor('calendar'),
    defaultSize: { w: 560, h: 520 },
    blurb: 'Presence events, eventgoer, forms, memories, guild, guestbook.',
    component: Calendar,
  },
  {
    id: 'community',
    label: 'Community',
    title: 'COMMUNITY.EXE',
    glyph: '◍',
    tile: tileFor('community'),
    defaultSize: { w: 540, h: 500 },
    blurb: 'Members, tiers, invites, perks.',
    component: Community,
  },
  {
    id: 'vendor',
    label: 'Vendor',
    title: 'VENDOR.EXE',
    glyph: '◫',
    tile: tileFor('vendor'),
    defaultSize: { w: 540, h: 500 },
    blurb: 'Vendors, onboarding, invoices, rate card.',
    component: Vendor,
  },

  /* ── Shell furniture ──────────────────────────────────────────────────── */
  {
    id: 'settings',
    label: 'Settings',
    title: 'SETTINGS.EXE',
    glyph: '⚙',
    tile: tileFor('settings'),
    defaultSize: { w: 560, h: 560 },
    blurb: 'Appearance — design language and the skin builder.',
    component: Settings,
  },
  {
    id: 'skin-builder',
    label: 'Skin builder',
    title: 'SKIN_BUILDER.EXE',
    glyph: '◑',
    // Its own crest, compiled by scripts/pixelgen.py (`dock_skin_builder`) into
    // public/pixel — the same two overlapping swatch plates as the desk's 24 px
    // FunctionMark. It used to borrow dock-dossier.png, which made two desks
    // read as one app; the wizard's tile was already compiled for that reason.
    tile: '/pixel/dock-skin-builder.png',
    defaultSize: { w: 620, h: 600 },
    blurb: 'Forge a skin — palette and the safe structural switches, applied at runtime.',
    component: SkinBuilder,
  },
  {
    id: 'wizard',
    label: 'Immersion',
    title: 'IMMERSION_WIZARD.EXE',
    glyph: '⚿',
    // The wizard postdates the design source's 13 apps, so it has no tile there.
    // Its mark is compiled by scripts/pixelgen.py (`dock_wizard`) rather than
    // borrowed, because the page standard requires a desk's crest to be its OWN
    // dock tile — and two tiles sharing dock-dossier would read as one app.
    tile: '/pixel/dock-wizard.png',
    defaultSize: { w: 760, h: 640 },
    blurb: 'Build an Immersion — identity, container, pillars, roster, skin.',
    component: ImmersionWizard,
  },
] as const;

export const IMPLEMENTED_APPS = APPS.filter((a) => a.component !== null);
export const PENDING_APPS = APPS.filter((a) => a.component === null);

export function appById(id: string): AppDef | undefined {
  return APPS.find((a) => a.id === id);
}

/** Where an unbuilt desk is specified, so the window can point at it. */
export const DESIGN_SOURCE = {
  repo: 'SupercomputeA/supercompute-immersion',
  file: (id: string) => `app-${id}.html`,
} as const;
